/*
  ACCESS TOKEN STORAGE. Read this before changing anything in this file.

  The access token lives in a module-scoped variable and NOWHERE ELSE. It is never written
  to localStorage, never to sessionStorage, never to IndexedDB, never to a cookie set by
  script, and never into the TanStack Query cache or any React state that could be
  serialised by a devtools snapshot.

  Why, from docs/07-identity-rbac.md section I.1:

    Access JWT   | 10 min        | memory in the SPA, never localStorage
    Refresh token| 14 days slide | HttpOnly; Secure; SameSite=Strict cookie, path-scoped
                                   to the refresh endpoint

  The refresh token is a cookie the browser attaches by itself. Script cannot read it, so
  cross-site scripting that can read every storage API still walks away with nothing
  durable: the access token dies with the tab, and the only thing that can mint a new one
  is a cookie the attacker cannot exfiltrate. On page load the SPA calls the refresh
  endpoint with that cookie and gets a fresh access token straight into memory.

  The consequence to accept, not work around: a full page reload starts with no access
  token and one refresh round trip. That is the price of the property, and adding a
  "remember me" that persists the access token would throw the property away.

  Refresh tokens rotate on every use, and presenting an already-rotated token is treated
  as theft: the server revokes the whole lineage. So the refresh call must be
  single-flight. Two concurrent 401s must not fire two refreshes, or the second one looks
  like a replay and logs the technician out of every session. That single-flight lives in
  client.ts and is the reason this store exposes a subscription rather than a bare getter.
*/

import type { IsoTimestamp } from './types';

export interface AccessToken {
  readonly value: string;
  /** Absolute expiry, from the token response, used to refresh proactively. */
  readonly expiresAt: IsoTimestamp;
}

type Listener = (token: AccessToken | null) => void;

let accessToken: AccessToken | null = null;
const listeners = new Set<Listener>();

export function getAccessToken(): AccessToken | null {
  return accessToken;
}

export function setAccessToken(next: AccessToken | null): void {
  accessToken = next;
  for (const listener of listeners) {
    listener(next);
  }
}

export function clearAccessToken(): void {
  setAccessToken(null);
}

/** Returns an unsubscribe function. Used by AuthProvider to react to a forced sign out. */
export function subscribeToAccessToken(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * True when the token is absent or within the skew window of expiry. The client refreshes
 * ahead of expiry rather than waiting for a 401, because a 401 mid-request means retrying
 * a request that may not be idempotent.
 */
export function isAccessTokenStale(skewSeconds = 45): boolean {
  if (accessToken === null) {
    return true;
  }
  const expiry = Date.parse(accessToken.expiresAt);
  if (Number.isNaN(expiry)) {
    return true;
  }
  return expiry - Date.now() <= skewSeconds * 1000;
}
